/**
 * Metrics tracking utilities
 */

import { Metrics, ConnectionState } from '../types';

/**
 * MetricsCollector class for tracking connection and message statistics
 */
export class MetricsCollector {
  private messagesReceived = 0;
  private messagesSent = 0;
  private connectionStartTime?: number;
  private totalUptime = 0;
  private reconnectAttempts = 0;
  private errors = 0;
  private errorsByType: Record<string, number> = {};
  private connectionState: ConnectionState = ConnectionState.DISCONNECTED;
  private lastConnectedAt?: number;
  
  // For HTTP latency tracking
  private latencies: number[] = [];
  private readonly maxLatencySamples = 100; // Keep last 100 samples

  /**
   * Record a message received
   */
  recordMessageReceived(): void {
    this.messagesReceived++;
  }

  /**
   * Record a message sent
   */
  recordMessageSent(): void {
    this.messagesSent++;
  }

  /**
   * Record a connection start
   */
  recordConnectionStart(): void {
    this.connectionStartTime = Date.now();
    this.lastConnectedAt = this.connectionStartTime;
    this.setConnectionState(ConnectionState.CONNECTED);
  }

  /**
   * Record a connection end
   */
  recordConnectionEnd(): void {
    if (this.connectionStartTime) {
      this.totalUptime += Date.now() - this.connectionStartTime;
      this.connectionStartTime = undefined;
    }
    if (this.connectionState === ConnectionState.CONNECTED) {
      this.setConnectionState(ConnectionState.DISCONNECTED);
    }
  }

  /**
   * Record a reconnection attempt
   */
  recordReconnectAttempt(): void {
    this.reconnectAttempts++;
    this.setConnectionState(ConnectionState.RECONNECTING);
  }

  /**
   * Record an error
   * @param errorType - Type of error (e.g., 'ConnectionError', 'ApiError')
   */
  recordError(errorType: string = 'Unknown'): void {
    this.errors++;
    this.errorsByType[errorType] = (this.errorsByType[errorType] || 0) + 1;
  }

  /**
   * Set the connection state
   * @param state - New connection state
   */
  setConnectionState(state: ConnectionState): void {
    this.connectionState = state;
  }

  /**
   * Record HTTP request latency
   * @param latency - Latency in milliseconds
   */
  recordLatency(latency: number): void {
    this.latencies.push(latency);
    
    // Keep only the last N samples
    if (this.latencies.length > this.maxLatencySamples) {
      this.latencies.shift();
    }
  }

  /**
   * Calculate average latency from recorded samples
   * @returns Average latency in milliseconds, or undefined if no samples
   */
  private calculateAverageLatency(): number | undefined {
    if (this.latencies.length === 0) {
      return undefined;
    }
    
    const sum = this.latencies.reduce((acc, val) => acc + val, 0);
    return sum / this.latencies.length;
  }

  /**
   * Get current uptime in milliseconds
   */
  private getCurrentUptime(): number {
    let uptime = this.totalUptime;
    if (this.connectionStartTime) {
      uptime += Date.now() - this.connectionStartTime;
    }
    return uptime;
  }

  /**
   * Get current metrics
   * @returns Metrics object
   */
  getMetrics(): Metrics {
    return {
      messagesReceived: this.messagesReceived,
      messagesSent: this.messagesSent,
      uptime: this.getCurrentUptime(),
      reconnectAttempts: this.reconnectAttempts,
      errors: this.errors,
      errorsByType: { ...this.errorsByType },
      connectionState: this.connectionState,
      lastConnectedAt: this.lastConnectedAt,
      averageLatency: this.calculateAverageLatency(),
    };
  }

  /**
   * Reset all metrics
   */
  reset(): void {
    this.messagesReceived = 0;
    this.messagesSent = 0;
    this.connectionStartTime = undefined;
    this.totalUptime = 0;
    this.reconnectAttempts = 0;
    this.errors = 0;
    this.errorsByType = {};
    this.connectionState = ConnectionState.DISCONNECTED;
    this.lastConnectedAt = undefined;
    this.latencies = [];
  }
}

